/**
 * Single source of truth for "is this the native iOS app, or the website."
 *
 * Every native-only gate in the codebase (purchase-path stripping, the
 * bottom tab bar, the ESPN desktop handoff, safe-area chrome, and so on)
 * must import IS_NATIVE_APP from here rather than calling
 * `Capacitor.isNativePlatform()` directly. Before this file existed, two
 * independent call sites (`services/api.js` and `services/storage.js`) each
 * called it themselves — harmless while there were only two, but every new
 * gate added its own call was a second place that could drift out of sync
 * (e.g. if a future Android build needed a different rule for one call site
 * but not another). One evaluation, one export, one place to change the
 * rule later.
 *
 * Deliberately named `runtime.js`, not `platform.js` — "platform" already
 * means ESPN/Yahoo/Sleeper everywhere else in this codebase
 * (`backend/src/services/platforms/`), so reusing the word here for
 * "native app vs. web" would be confusing. Never call this file or its
 * exports "platform" in code or comments.
 *
 * Never use user-agent sniffing as a substitute for this check — Capacitor's
 * own runtime flag is the only source of truth the codebase uses.
 */

import { Capacitor } from '@capacitor/core'

/** True when running inside the native iOS (or Android, if ever added) app shell. */
export const IS_NATIVE_APP = Capacitor.isNativePlatform()

/** The underlying OS Capacitor reports ('ios', 'android', or 'web'). */
export const DEVICE_OS = Capacitor.getPlatform()

/** True only when running inside the native iOS app specifically. */
export const IS_IOS_APP = IS_NATIVE_APP && DEVICE_OS === 'ios'

/**
 * Pulls a user-facing error string off an axios error, the same way most of
 * the codebase already does (`err.response?.data?.error || fallback`) —
 * except on native, where a `PLAN_UPGRADE_REQUIRED` response is replaced
 * with a fixed neutral sentence first. Several of the server's own
 * PLAN_UPGRADE_REQUIRED messages say things like "...upgrade to connect
 * more" (planAccessMiddleware.js) — fine on web, not allowed to render
 * inside the iOS app under Guideline 3.1.1. Use this instead of reading
 * `err.response.data.error` directly anywhere the error could plausibly be
 * a plan/limit block, not just a generic failure.
 */
export function nativeSafeErrorMessage(err, fallback) {
  const data = err?.response?.data
  if (IS_NATIVE_APP && data?.code === 'PLAN_UPGRADE_REQUIRED') {
    return "This isn't part of your current plan."
  }
  return data?.error || fallback
}
